import { useMemo } from 'react';
import { useDebounce } from '@/shared/lib';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import type { AssetOptionWithBalance, NetworkOption } from '../model/types';
import { useWithdrawCommission } from '../api/queries';

interface UseCommissionDataParams {
  hasAmountError: boolean;
  selectedAsset?: AssetOptionWithBalance;
  selectedNetwork?: NetworkOption;
  amount: number;
  onTop?: boolean;
}

export function useCommissionData({
  hasAmountError,
  selectedAsset,
  selectedNetwork,
  amount,
  onTop,
}: UseCommissionDataParams) {
  const debouncedAmount = useDebounce(amount, COMMISSION_CHECK_DELAY);
  const isAmountDebouncing = amount !== debouncedAmount;

  const commissionEnabled = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    if (isAmountDebouncing) {
      return false;
    }

    if (!debouncedAmount) {
      return false;
    }

    if (!selectedAsset?.code || !selectedNetwork?.name) {
      return false;
    }

    return true;
  }, [
    debouncedAmount,
    hasAmountError,
    isAmountDebouncing,
    selectedAsset?.code,
    selectedNetwork?.name,
    onTop,
  ]);

  const { commissionData, commissionError, isCommissionFetching } =
    useWithdrawCommission({
      networkName: selectedNetwork?.name || '',
      assetCode: selectedAsset?.code || '',
      amount: debouncedAmount,
      onTop: onTop,
      enabled: commissionEnabled,
    });

  const isGettingCommission = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const hasAmount = !!amount;
    const hasAsset = !!selectedAsset?.code;
    const hasNetwork = !!selectedNetwork?.name;

    return (
      (isAmountDebouncing && hasAmount && hasAsset && hasNetwork) ||
      isCommissionFetching
    );
  }, [
    amount,
    debouncedAmount,
    selectedAsset?.code,
    selectedNetwork?.name,
    hasAmountError,
    isAmountDebouncing,
    isCommissionFetching,
  ]);

  return {
    commissionData,
    commissionError,
    isGettingCommission,
  };
}
